"use client";

import { useEffect } from "react";
import { useRouter as useNativeRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Link, usePathname, useRouter, getPathname } from "@/i18n/navigation";
import {
  localeFullLabels,
  localeShortLabels,
  type Locale,
  locales,
} from "@/i18n/config";

type Variant = "header" | "footer";

interface LanguageSwitcherProps {
  variant?: Variant;
}

export function LanguageSwitcher({ variant = "header" }: LanguageSwitcherProps) {
  const t = useTranslations("Common");
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const nativeRouter = useNativeRouter();

  // next-intl Link with `locale` forces prefetch={false}. Warm the other
  // locales via the native router so a click does not wait on a cold RSC.
  useEffect(() => {
    for (const code of locales) {
      if (code === locale) continue;
      nativeRouter.prefetch(
        getPathname({ href: pathname, locale: code, forcePrefix: true })
      );
    }
  }, [locale, pathname, nativeRouter]);

  if (variant === "footer") {
    return (
      <nav
        aria-label={t("language")}
        className="flex flex-wrap items-center gap-3 text-sm"
      >
        {locales.map((code) => (
          <Link
            key={code}
            href={pathname}
            locale={code}
            replace
            scroll={false}
            className={
              code === locale
                ? "text-teal-light font-medium"
                : "text-slate-400 hover:text-slate-100 transition-colors"
            }
            aria-current={code === locale ? "page" : undefined}
          >
            {localeFullLabels[code]}
          </Link>
        ))}
      </nav>
    );
  }

  return (
    <>
      <label className="relative sm:hidden">
        <span className="sr-only">{t("language")}</span>
        <select
          value={locale}
          aria-label={t("language")}
          onChange={(e) => {
            router.replace(pathname, { locale: e.target.value as Locale });
          }}
          className="appearance-none rounded-md border border-slate-700 bg-slate-800 py-1.5 pl-2 pr-6 text-[11px] font-semibold text-slate-200"
        >
          {locales.map((code) => (
            <option key={code} value={code}>
              {localeShortLabels[code]}
            </option>
          ))}
        </select>
        <span
          aria-hidden
          className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-400"
        >
          ▾
        </span>
      </label>
      <div
        role="group"
        aria-label={t("language")}
        className="hidden shrink-0 rounded-lg border border-slate-700 bg-slate-800/60 p-0.5 sm:inline-flex"
      >
        {locales.map((code) => (
          <Link
            key={code}
            href={pathname}
            locale={code}
            replace
            scroll={false}
            title={localeFullLabels[code]}
            aria-label={localeFullLabels[code]}
            aria-current={code === locale ? "page" : undefined}
            className={`min-w-[2rem] rounded-md px-2.5 py-1.5 text-xs font-semibold tracking-wide transition-colors ${
              code === locale
                ? "border border-teal-primary/50 bg-teal-primary/25 text-teal-light"
                : "border border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            {localeShortLabels[code]}
          </Link>
        ))}
      </div>
    </>
  );
}
